import React, { useEffect, useRef, useState } from 'react';

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

type Props = {
  previewHtml: string | null;
  refreshing: boolean;
  error: string | null;
  showLogoPrompt: boolean;
  onRetry: () => void;
  onPreviewReady: () => void;
  onUploadLogoClick: () => void;
};

export default function PdfStudioPreview(props: Props): React.JSX.Element {
  const {
    previewHtml,
    refreshing,
    error,
    showLogoPrompt,
    onRetry,
    onPreviewReady,
    onUploadLogoClick
  } = props;
  const centerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = centerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth - 48;
      setScale(Math.min(1, Math.max(0.35, w / A4_WIDTH)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!previewHtml) return;
    const frame = iframeRef.current;
    if (!frame) return;
    const onLoad = () => onPreviewReady();
    frame.addEventListener('load', onLoad);
    return () => frame.removeEventListener('load', onLoad);
  }, [previewHtml, onPreviewReady]);

  if (error) {
    return (
      <main className="pdf-studio-center" ref={centerRef}>
        <div className="pdf-studio-preview-error">
          <p style={{ margin: 0, fontWeight: 600, color: '#0f172a' }}>Preview unavailable</p>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>{error}</p>
          <button type="button" className="btn-primary" onClick={onRetry}>
            Retry preview
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="pdf-studio-center" ref={centerRef}>
      <div
        className="pdf-studio-preview-scaler"
        style={{ transform: `scale(${scale})`, width: A4_WIDTH }}
      >
        <div className="pdf-studio-preview-wrap">
          {refreshing ? (
            <div className="pdf-studio-preview-spinner" aria-hidden>
              <div className="pdf-studio-spinner" />
            </div>
          ) : null}
          {showLogoPrompt ? (
            <div className="pdf-studio-logo-prompt">
              <div style={{ fontWeight: 600, color: '#334155' }}>No logo uploaded</div>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Add your company logo to the PDF header
              </p>
              <button type="button" className="btn-secondary" onClick={onUploadLogoClick}>
                Upload logo
              </button>
            </div>
          ) : null}
          <div className="pdf-studio-a4-frame">
            {previewHtml ? (
              <iframe
                ref={iframeRef}
                title="PDF preview"
                srcDoc={previewHtml}
                sandbox="allow-same-origin"
                style={{ width: A4_WIDTH, height: A4_HEIGHT }}
              />
            ) : (
              <div
                className="pdf-studio-skeleton pdf-studio-skeleton-preview"
                style={{ width: A4_WIDTH, height: A4_HEIGHT }}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
